import React, { FC, useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import OtpInput from 'react-otp-input';
import { useForm, SubmitHandler, Controller } from "react-hook-form";

import '../update-info-modal/update-info-modal.scss';
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { authActions, changePasswordStatus, getUserUpdateStatus, getUserVerifyOtpStatus } from "../../../../reducers/authSlice";
import { useTranslation } from 'react-i18next';


interface ChangePasswordPayload {
  email : string;
  existingPassword : string;
  newPassword:string;
  confirmPassword:string;
}

interface ChangeEmailOtpVerificationPayload {
  otp : string;
  emailPayloadKey: string
}

interface UpdatePasswordModalProps {
  hasModalVisible: boolean;
  handleClose: (arg: any) => void;
  emailPayloadKey: string;
}

const UpdatePasswordModal: FC<UpdatePasswordModalProps> = ({
  hasModalVisible, 
  handleClose,
  emailPayloadKey
}) => {
  const dispatch = useAppDispatch();
  const userProfileUpdateStatus : string = useAppSelector(getUserUpdateStatus);
  const userVerifyOtpStatus: string = useAppSelector(getUserVerifyOtpStatus);
  const changePassStatus: string = useAppSelector(changePasswordStatus);


  const [showVerifyOTP, setShowVerifyOTP] = useState(false);
  const [showConfirmationStep, setShowConfirmationStep] = useState(false);
  const [OTP, setOTP] = useState("");
  const [showTimer, setShowTimer] = useState(true);
  const [time, setTime] = useState(180);
  const [minutes, setMinutes] = useState(Math.floor(time / 60));
  const [seconds, setSeconds] = useState(time % 60);
  const [ updatedEmail, setUpdatedEmail ] = useState("");

  const {
    register: changePasswordRegister,
    handleSubmit: handChangePasswordSubmit,
    formState: { errors: errorsChangePasswordErrors },
    reset: resetPasswordForm,
    getValues,
  } = useForm<ChangePasswordPayload>();
  const changePasswordSubmit: SubmitHandler<ChangePasswordPayload> = (data) => onSubmit(data);

  const {
    register: verifyOtpRegister,
    handleSubmit: handVerifyOtpSubmit,
    formState: { errors: errorsVerifyOtpErrors },
    reset: verifyOtpForm,
    control: verifyOtpControl,
  } = useForm<ChangeEmailOtpVerificationPayload>();
  const verifyOtpFormSubmit: SubmitHandler<ChangeEmailOtpVerificationPayload> = () => verifyOTPEvent();

  const closePopup = () => {
    handleClose(!hasModalVisible);
    setShowConfirmationStep(false);
    setShowVerifyOTP(false);
    setUpdatedEmail("");
    setOTP("")
    resetPasswordForm();
  };

  const verifyOTPEvent = () => {
    const validateOtpPayload = { verificationCode: OTP, verificationType: emailPayloadKey };
    dispatch(authActions.updateUserOtpVerification(validateOtpPayload));
    setUpdatedEmail("");
  };

  // move to next step when there is a successfull update
  useEffect(() => {
    
    if (hasModalVisible && userProfileUpdateStatus == "success" && !showVerifyOTP && !showConfirmationStep) {
      setShowConfirmationStep(false);
      setShowVerifyOTP(true);
      dispatch(authActions.resetUserUpdateStatus());
    } else if (hasModalVisible && userVerifyOtpStatus == "success" && showVerifyOTP && !showConfirmationStep) {
      setShowConfirmationStep(true);
      setShowVerifyOTP(false);
      dispatch(authActions.resetUserVerifyOtpStatus());
    }

  }, [hasModalVisible, userProfileUpdateStatus, userVerifyOtpStatus , showVerifyOTP, showConfirmationStep])


  function padLeadingZeros(num: any, size: any) {
    let s = num + "";
    while (s.length < size) s = "0" + s;
    return s;
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(time - 1);
      setMinutes(Math.floor(time / 60));
      setSeconds(time % 60);
    }, 1000);
    if (time < 0) {
      setShowTimer(false);
    }

    return () => clearInterval(interval);
  }, [time, minutes, seconds]);

  useEffect(()=>{
    if(changePassStatus==="success"){
        handleClose(false);
        resetPasswordForm();
    }
  },[changePassStatus])

  const onSubmit = (data:any) => {
    let obj={
        currentPassword: data?.existingPassword,
        newPassword: data?.newPassword
    }
    dispatch(authActions.changePassword(obj));
    // const updatePayload = emailPayloadKey == 'secondaryEmail' ? { secondaryEmail: updatedEmail } : { email: updatedEmail };
    // dispatch(authActions.updateProfileData(updatePayload));
  }

  const resend = () => {
    /**
     * fix: IN-300
     */
    const updatePayload = emailPayloadKey == 'secondaryEmail' ? { secondaryEmail: updatedEmail } : { email: updatedEmail };
    dispatch(authActions.updateProfileData(updatePayload));
    setTime(180);
    setMinutes(Math.floor(180 / 60));
    setSeconds(180 % 60);
    setShowTimer(true);
  }

  const {t}=useTranslation("")


  return (
    <>
      <Modal
        className="updateInfoModalWrapper"
        show={hasModalVisible}
        centered={true}
      >
        <Modal.Body>
          <a href={void 0} className="closeButton" onClick={closePopup}>
            <em className="fa-solid fa-close"></em>
          </a>
          <div className="formWrap">
            {!showVerifyOTP && !showConfirmationStep && (
              <form className="formBlock" onSubmit={handChangePasswordSubmit(changePasswordSubmit)}>
                <h4 className="mb-3">{t("UpdatePasswordModal.title")}</h4>
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="password"
                      className="form-control"
                      id="existingPassword"
                      placeholder={t("UpdatePasswordModal.formFields.formField1.placeholder")}
                      {...changePasswordRegister("existingPassword", { 
                          required: true, 
                      })}
                    />
                    <label htmlFor="existingPassword">{t("UpdatePasswordModal.formFields.formField1.label")}</label>
                  </div>
                  {
                    errorsChangePasswordErrors.existingPassword?.type == "required" && (
                          <span className="isInvalidMessage text-danger">
                              {t("UpdatePasswordModal.formFields.formField1.requiredError")}
                          </span>
                      )
                  }
                </div>
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="password"
                      className="form-control"
                      id="newPassword"
                      placeholder={t("UpdatePasswordModal.formFields.formField2.placeholder")}
                      {...changePasswordRegister("newPassword", { 
                          required: true, 
                          validate: (value:any) => {
                            const trimmedValue = value.trim();
                            if(trimmedValue.length < 8){
                              return t("UpdatePasswordModal.formFields.formField2.minLengthError");
                            }else if(getValues("existingPassword") && getValues("existingPassword") === value){
                                return t("UpdatePasswordModal.formFields.formField2.sameMatchError")
                            }
                            else if(getValues("confirmPassword") && getValues("confirmPassword") !== value){
                                return t("UpdatePasswordModal.formFields.formField2.matchError")
                            }
                            return true;
                          }
                      })}
                     
                    />
                    <label htmlFor="newPassword">{t("UpdatePasswordModal.formFields.formField2.label")}</label>
                  </div>
                  {
                    errorsChangePasswordErrors.newPassword?.type == "required" && (
                          <span className="isInvalidMessage text-danger">
                              {t("UpdatePasswordModal.formFields.formField2.requiredError")}
                          </span>
                      )
                  }
                    {errorsChangePasswordErrors.newPassword?.type == "validate" && <span className="isInvalidMessage text-danger">{errorsChangePasswordErrors.newPassword?.message}</span>}

                </div>
                <div className="mb-4">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control"
                      id="confirmPassword"
                      placeholder={t("UpdatePasswordModal.formFields.formField3.placeholder")}
                      {...changePasswordRegister("confirmPassword", { 
                          required: true, 
                          validate: (value:any) => {
                            const trimmedValue = value.trim();
                            if(trimmedValue.length < 8){
                              return t("UpdatePasswordModal.formFields.formField3.minLengthError");
                            }else if(getValues("existingPassword") && getValues("existingPassword") === value){
                                return t("UpdatePasswordModal.formFields.formField3.sameMatchError")
                            }
                            else if(getValues("newPassword") && getValues("newPassword") !== value){
                                return t("UpdatePasswordModal.formFields.formField3.matchError")
                            }
                            return true;
                          }
                      })}
                    />
                    <label htmlFor="confirmNewPassword">{t("UpdatePasswordModal.formFields.formField3.label")}</label>
                  </div>
                  {
                    errorsChangePasswordErrors.confirmPassword?.type == "required" && (
                          <span className="isInvalidMessage text-danger">
                              {t("UpdatePasswordModal.formFields.formField3.requiredError")}
                          </span>
                      )
                  }
                    {errorsChangePasswordErrors.confirmPassword?.type == "validate" && <span className="isInvalidMessage text-danger">{errorsChangePasswordErrors.confirmPassword?.message}</span>}

                </div>
                <button
                  type="submit"
                  className="button button-primary button-rounded button-min-150 button-large px-2 fontsize-16 fw700 mb-2"
                >
                  {t("UpdatePasswordModal.formFields.Submit")}
                </button>
              </form>
            )}
            {showVerifyOTP && !showConfirmationStep && (
              <form className="formOtpVerification" onSubmit={handVerifyOtpSubmit(verifyOtpFormSubmit)}>
                <div className="formBlock pb-0">
                  <h4 className="mb-3">{t("UpdatePasswordModal.VerifyEmailPopup.title")}</h4>
                  <p>{t("UpdatePasswordModal.VerifyEmailPopup.description")}</p>
                  <div className="mb-4">
                    <Controller 
                      control={verifyOtpControl}
                      {...verifyOtpRegister("otp", { required: true, minLength: 4 })} 
                      render={({ field: { onChange } }) => <>
                        <OtpInput
                          value={OTP}
                          shouldAutoFocus={true}
                          numInputs={4}
                          containerStyle={"optInputWrap justify-content-between"}
                          inputStyle={"optInput"}
                          focusStyle={"optInputFocus"}
                          isInputNum={true}
                          placeholder=""
                          onChange={(value: string) => {
                            onChange(value);
                            setOTP(value);
                          }}
                        />
                      </>} 
                    />
                  </div>
                  {
                    errorsVerifyOtpErrors.otp?.type == "required" && (
                      <span className="isInvalidMessage text-danger">
                        {t("UpdatePasswordModal.VerifyEmailPopup.requiredOTPError")}
                      </span>
                    )
                  }
                  {
                    errorsVerifyOtpErrors.otp?.type == "minLength" && (
                      <span className="isInvalidMessage text-danger">
                        {t("UpdatePasswordModal.VerifyEmailPopup.minLengthError")}
                      </span>
                    )
                  }
                  <div className="d-flex justify-content-between align-items-center mb-4 pb-3">
                    {showTimer && <p className="mb-0 fw600">{t("UpdatePasswordModal.VerifyEmailPopup.TimeRemaining")} {padLeadingZeros(minutes, 2)} : {padLeadingZeros(seconds, 2)} </p>}
                    {!showTimer && <p className="mb-0 fw600">{t("UpdatePasswordModal.VerifyEmailPopup.TimeRemaining")} 00:00 </p>}
                      <button
                        type="button"
                        className="resendLink fw600 color-gray"
                        onClick={resend}
                        disabled={(minutes+seconds > 0)}
                      >
                        {t("UpdatePasswordModal.VerifyEmailPopup.Resend")}
                      </button>
                  </div>
                  <button
                    type="submit"
                    className="button button-primary button-rounded button-min-150 button-large px-2 fontsize-16 fw700 mb-2"
                  >
                    {t("UpdatePasswordModal.VerifyEmailPopup.Verify")}
                  </button>
                </div>
              </form>
            )}
            {!showVerifyOTP && showConfirmationStep && (
              <div className="successConfirmationModalWrap text-center">
                <span className="markIcon success">
                  <em className="fa-solid fa-circle-check"></em>
                </span>
                <h2 className="mb-2">{t("UpdatePasswordModal.VerifyEmailPopup.AllDoneText")}</h2>
                <p className="color-gray fw500">{t("UpdatePasswordModal.VerifyEmailPopup.updateMessage")}</p>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UpdatePasswordModal;
